import * as React from 'react'
import FormikMoneyField from '../../Formik/FormikMoneyField'
import { ReactElement } from 'react'
import { useFormikContext } from 'formik'
import { FileMutationDto } from '../../../config/fileConfig'
import { BudgetMonitoringModuleDto, useFileModule } from '../../../hooks/module/useFileModule'
import { ModuleType } from '../../../context/GlobalContext'
import { FieldProps } from '@fluentui/react-field'
import { Field, MessageBar, MessageBarBody, Switch, SwitchOnChangeData } from '@fluentui/react-components'
import FactorInput from './Insolvency/FactorInput'
import { useTranslation } from 'react-i18next'

type BudgetMonitoringFieldsProps = {
    styles: Record<'formRow', string>
    orientation: FieldProps['orientation']
}

function FormikSwitchField(
    props: Omit<FieldProps, 'label' | 'onChange'> & {
        labelKey: string
        checked: boolean
        onChange: (ev: React.ChangeEvent<HTMLInputElement>, data: SwitchOnChangeData) => void
        disabled?: boolean
    },
) {
    const { t: translate } = useTranslation()

    return (
        <Field label={props.labelKey ? translate(props.labelKey) : undefined} {...props} onChange={undefined}>
            <Switch disabled={props.disabled} checked={props.checked} onChange={props.onChange} />
        </Field>
    )
}

export default function BudgetMonitoringFields({ styles, orientation }: BudgetMonitoringFieldsProps): ReactElement {
    const { values, setFieldValue } = useFormikContext<FileMutationDto>()
    const { has: hasFileModule, fileModule, index: fileModuleIndex } = useFileModule<BudgetMonitoringModuleDto>(ModuleType.BudgetMonitoring, values)
    const { t: translate } = useTranslation()

    if (!hasFileModule) {
        return <></>
    }

    return (
        <div className={styles.formRow}>
            <MessageBar intent={'info'} layout={'multiline'}>
                <MessageBarBody>{translate('file.edit.form.finance.budgetmonitoring.info')}</MessageBarBody>
            </MessageBar>
            <Field label={translate('file.edit.form.finance.budgetmonitoring.hourthreshold')} orientation={orientation}>
                <FactorInput
                    required
                    step={1}
                    precision={1}
                    max={999}
                    value={`${fileModule.hourThreshold}`}
                    onChange={value => {
                        setFieldValue(`modules[${fileModuleIndex}.hourThreshold]`, value)
                    }}
                />
            </Field>
            <FormikMoneyField<any>
                orientation={orientation}
                labelKey={'file.edit.form.finance.budgetmonitoring.pricethreshold'}
                fieldName={`modules[${fileModuleIndex}].priceThreshold`}
                currency={values.currency}
                align={'end'}
                selectOnFocus
            />

            <FormikSwitchField
                disabled
                orientation={orientation}
                labelKey={'file.edit.form.finance.budgetmonitoring.includehours'}
                checked={fileModule.includeHours}
                onChange={(_, data: SwitchOnChangeData) => {
                    fileModule.includeHours = data.checked
                    setFieldValue(`modules[${fileModuleIndex}.includeHours]`, data.checked)
                }}
            />
            <FormikSwitchField
                orientation={orientation}
                labelKey={'file.edit.form.finance.budgetmonitoring.includetravels'}
                checked={fileModule.includeTravels}
                onChange={(_, data: SwitchOnChangeData) => {
                    fileModule.includeTravels = data.checked
                    setFieldValue(`modules[${fileModuleIndex}.includeTravels]`, data.checked)
                }}
            />
            <FormikSwitchField
                orientation={orientation}
                labelKey={'file.edit.form.finance.budgetmonitoring.includedisbursements'}
                checked={fileModule.includeDisbursements}
                onChange={(_, data: SwitchOnChangeData) => {
                    fileModule.includeDisbursements = data.checked
                    setFieldValue(`modules[${fileModuleIndex}.includeDisbursements]`, data.checked)
                }}
            />

            <FormikSwitchField
                orientation={orientation}
                labelKey={'file.edit.form.finance.budgetmonitoring.includeofferedhours'}
                checked={fileModule.includeOfferedHours}
                onChange={(_, data: SwitchOnChangeData) => {
                    fileModule.includeOfferedHours = data.checked
                    setFieldValue(`modules[${fileModuleIndex}.includeOfferedHours]`, data.checked)
                }}
            />
            <FormikSwitchField
                orientation={orientation}
                labelKey={'file.edit.form.finance.budgetmonitoring.includebilled'}
                checked={fileModule.includeBilled}
                onChange={(_, data: SwitchOnChangeData) => {
                    fileModule.includeBilled = data.checked
                    setFieldValue(`modules[${fileModuleIndex}.includeBilled]`, data.checked)
                }}
            />
        </div>
    )
}
